import { useEffect, useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useOrg } from '../lib/org';
import { useAuth } from '../lib/auth';
import type { Organization, VatPeriod } from '../lib/types';
import { Plus, Landmark, UserPlus, X, Trash2, Paintbrush } from 'lucide-react';
import InvoiceDesigner from './InvoiceDesigner';

export default function Settings() {
  const { currentOrg, refresh, isPlatformAdmin } = useOrg();
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') ?? 'business';

  // Build tab list — Team always visible, +New organization only for platform admin
  const tabs: { key: string; label: string }[] = [
    { key: 'business', label: 'Business details' },
    { key: 'tax', label: 'Tax & VAT' },
    { key: 'payment', label: 'Payment instructions' },
    { key: 'design', label: 'Invoice design' },
    { key: 'team', label: 'Team' },
  ];

  return (
    <div className="flex flex-col gap-6">
      <header>
        <h1 className="h-page">Settings</h1>
      </header>

      <div className="flex gap-1 border-b border-line">
        {tabs.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setParams({ tab: t.key })}
            className={
              'px-4 py-2 text-sm border-b-2 -mb-px ' +
              (tab === t.key ? 'border-emerald text-emerald' : 'border-transparent text-text-dim hover:text-text')
            }
          >{t.label}</button>
        ))}
      </div>

      {tab === 'business' && currentOrg && <BusinessTab org={currentOrg} onSaved={refresh} />}
      {tab === 'tax' && currentOrg && <TaxTab org={currentOrg} onSaved={refresh} />}
      {tab === 'payment' && currentOrg && <PaymentInstructionsTab org={currentOrg} />}
      {tab === 'design' && currentOrg && <InvoiceDesigner />}
      {tab === 'team' && currentOrg && <TeamTab org={currentOrg} />}
    </div>
  );
}

interface Member {
  user_id: string;
  email: string;
  role: string;
  joined_at: string;
  is_owner: boolean;
}

function TeamTab({ org }: { org: Organization }) {
  const { user } = useAuth();
  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);
  const [inviting, setInviting] = useState(false);
  const [busyUserId, setBusyUserId] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function refresh() {
    setLoading(true);
    const { data, error } = await supabase.rpc('dodobooks_list_members', { p_org_id: org.id });
    if (error) setErr(error.message);
    else setMembers((data ?? []) as Member[]);
    setLoading(false);
  }
  useEffect(() => { refresh(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [org.id]);

  const myMembership = members.find((m) => m.user_id === user?.id);
  const canManage = myMembership?.role === 'admin' || myMembership?.is_owner;

  async function changeRole(m: Member, role: string) {
    if (!canManage || m.is_owner) return;
    setBusyUserId(m.user_id); setErr(null); setMsg(null);
    const { error } = await supabase.from('dodobooks_org_members').update({ role }).eq('org_id', org.id).eq('user_id', m.user_id);
    setBusyUserId(null);
    if (error) setErr(error.message);
    else { setMsg('Role updated.'); refresh(); }
  }

  async function removeMember(m: Member) {
    if (!canManage || m.is_owner) return;
    if (!confirm('Remove ' + m.email + ' from this company? They will lose access immediately.')) return;
    setBusyUserId(m.user_id); setErr(null); setMsg(null);
    const { error } = await supabase.from('dodobooks_org_members').delete().eq('org_id', org.id).eq('user_id', m.user_id);
    setBusyUserId(null);
    if (error) setErr(error.message);
    else { setMsg('Member removed.'); refresh(); }
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="card">
        <div className="flex items-start gap-3 text-sm">
          <UserPlus size={20} className="text-emerald mt-0.5 shrink-0" />
          <div className="flex-1">
            <div className="font-semibold">Team members for {org.name}</div>
            <p className="text-text-dim text-xs mt-0.5">
              {canManage
                ? 'Add a user and they can sign in immediately with the password you set. They\'ll only see this company.'
                : 'You\'re a member of this company. Only admins can invite new members.'}
            </p>
          </div>
          {canManage && (
            <button onClick={() => setInviting(true)} className="btn btn-primary"><UserPlus size={14} /> Invite user</button>
          )}
        </div>
      </div>

      {err && <div className="text-sm text-rust bg-rust/10 border border-rust/30 px-3 py-2 rounded-md">{err}</div>}
      {msg && <div className="text-sm text-emerald bg-emerald/10 border border-emerald/30 px-3 py-2 rounded-md">{msg}</div>}

      <div className="card p-0">
        {loading ? (
          <div className="text-text-mute text-sm py-12 text-center">Loading…</div>
        ) : members.length === 0 ? (
          <div className="text-text-mute text-sm py-12 text-center">No members yet — odd, you should be one.</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th className="px-5">Email</th>
                <th>Role</th>
                <th>Joined</th>
                <th className="text-right pr-5"></th>
              </tr>
            </thead>
            <tbody>
              {members.map((m) => (
                <tr key={m.user_id}>
                  <td className="px-5">
                    {m.email}
                    {m.is_owner && <span className="pill pill-paid ml-2">Owner</span>}
                    {m.user_id === user?.id && <span className="text-text-mute font-mono text-xs ml-2">(you)</span>}
                  </td>
                  <td>
                    {canManage && !m.is_owner ? (
                      <select
                        value={m.role}
                        onChange={(e) => changeRole(m, e.target.value)}
                        disabled={busyUserId === m.user_id}
                        className="text-xs"
                      >
                        <option value="admin">Admin</option>
                        <option value="editor">Editor</option>
                        <option value="viewer">Viewer</option>
                      </select>
                    ) : (
                      <span className="pill pill-sent">{m.role}</span>
                    )}
                  </td>
                  <td className="text-text-mute font-mono text-xs">{new Date(m.joined_at).toLocaleDateString('en-ZA')}</td>
                  <td className="text-right pr-5">
                    {canManage && !m.is_owner && m.user_id !== user?.id && (
                      <button onClick={() => removeMember(m)} disabled={busyUserId === m.user_id}
                        className="text-text-mute hover:text-rust disabled:opacity-30" title="Remove from company">
                        <Trash2 size={14} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {inviting && (
        <InviteUserDialog
          orgId={org.id}
          onClose={() => setInviting(false)}
          onInvited={() => { setInviting(false); setMsg('User added. They can sign in immediately.'); refresh(); }}
        />
      )}
    </div>
  );
}

function InviteUserDialog({ orgId, onClose, onInvited }: { orgId: string; onClose: () => void; onInvited: () => void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [role, setRole] = useState<'admin' | 'editor' | 'viewer'>('admin');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!email.trim() || password.length < 6) {
      setErr('Email and a password (6+ chars) are required.');
      return;
    }
    setBusy(true); setErr(null);
    const { data, error } = await supabase.functions.invoke('dodobooks-create-user', {
      body: {
        email: email.trim().toLowerCase(),
        password,
        full_name: fullName.trim() || null,
        org_id: orgId,
        role,
      },
    });
    setBusy(false);
    if (error) { setErr(error.message); return; }
    if (data?.error) { setErr(data.error); return; }
    onInvited();
  }

  return (
    <div className="fixed inset-0 z-50 bg-ink/80 backdrop-blur-sm grid place-items-center px-4">
      <div className="bg-surface border border-line-strong rounded-xl w-full max-w-md">
        <div className="flex items-center justify-between p-5 border-b border-line">
          <h2 className="h-section">Invite a user</h2>
          <button onClick={onClose} className="text-text-mute hover:text-text"><X size={18} /></button>
        </div>
        <form onSubmit={onSubmit} className="p-5 grid grid-cols-2 gap-4">
          <div className="field col-span-2"><label>Email *</label><input required autoFocus type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="teammate@business.co.za" /></div>
          <div className="field col-span-2"><label>Temporary password *</label><input required type="text" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="6+ characters" /></div>
          <div className="field col-span-2"><label>Full name (optional)</label><input value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="Jane Smith" /></div>
          <div className="field col-span-2">
            <label>Role</label>
            <select value={role} onChange={(e) => setRole(e.target.value as any)}>
              <option value="admin">Admin — full access including team management</option>
              <option value="editor">Editor — create + edit, can't manage team</option>
              <option value="viewer">Viewer — read-only</option>
            </select>
          </div>
          <p className="col-span-2 text-xs text-text-mute">
            We'll create their login right now. Share the temporary password with them through a private channel. They can change it later.
          </p>
          {err && <div className="col-span-2 text-sm text-rust bg-rust/10 border border-rust/30 px-3 py-2 rounded-md">{err}</div>}
          <div className="col-span-2 flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="btn btn-ghost">Cancel</button>
            <button type="submit" disabled={busy} className="btn btn-primary disabled:opacity-50"><UserPlus size={14} /> {busy ? 'Creating…' : 'Add user'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

interface PaymentInstructions {
  bank_name: string;
  account_holder: string;
  account_number: string;
  branch_code: string;
  account_type: string;
  swift: string;
  reference_hint: string;
}

const EMPTY_PAY: PaymentInstructions = {
  bank_name: '', account_holder: '', account_number: '',
  branch_code: '', account_type: 'Cheque', swift: '', reference_hint: '',
};

function PaymentInstructionsTab({ org }: { org: Organization }) {
  const [form, setForm] = useState<PaymentInstructions>(EMPTY_PAY);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      setLoaded(false);
      const { data } = await supabase
        .from('dodobooks_settings')
        .select('value')
        .eq('org_id', org.id)
        .eq('key', 'payment_instructions')
        .maybeSingle();
      const v = (data?.value ?? {}) as Partial<PaymentInstructions>;
      setForm({
        bank_name: v.bank_name ?? '',
        account_holder: v.account_holder ?? org.legal_name ?? org.name,
        account_number: v.account_number ?? '',
        branch_code: v.branch_code ?? '',
        account_type: v.account_type ?? 'Cheque',
        swift: v.swift ?? '',
        reference_hint: v.reference_hint ?? 'Your invoice number',
      });
      setLoaded(true);
    })();
  }, [org.id, org.legal_name, org.name]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true); setErr(null); setMsg(null);
    // Upsert by (org_id, key) — settings has a composite PK on (org_id, key).
    const { error } = await supabase
      .from('dodobooks_settings')
      .upsert({
        org_id: org.id,
        key: 'payment_instructions',
        value: form as any,
        updated_at: new Date().toISOString(),
      }, { onConflict: 'org_id,key' });
    setBusy(false);
    if (error) setErr(error.message);
    else setMsg('Saved. Customers will see these details on every invoice.');
  }

  async function clearAll() {
    if (!confirm('Remove payment instructions? The "How to pay" panel will stop appearing on customer invoices.')) return;
    setBusy(true); setErr(null); setMsg(null);
    const { error } = await supabase
      .from('dodobooks_settings')
      .delete()
      .eq('org_id', org.id)
      .eq('key', 'payment_instructions');
    setBusy(false);
    if (error) setErr(error.message);
    else { setForm(EMPTY_PAY); setMsg('Cleared.'); }
  }

  const filled = form.bank_name.trim() && form.account_number.trim();

  return (
    <form onSubmit={onSubmit} className="card grid grid-cols-2 gap-4">
      <div className="col-span-2 flex items-start gap-3 bg-emerald/5 border border-emerald/20 rounded-md p-3">
        <Landmark size={18} className="text-emerald mt-0.5 shrink-0" />
        <div>
          <div className="text-sm font-semibold">EFT / banking details for your invoices</div>
          <p className="text-xs text-text-dim mt-0.5">
            Customers see this in a "How to pay" panel at the bottom of every invoice you send them.
            The reference field tells the customer what to put in their EFT reference so payments match up easily.
          </p>
        </div>
      </div>

      <div className="field col-span-2">
        <label>Account holder *</label>
        <input required value={form.account_holder} onChange={(e) => setForm({ ...form, account_holder: e.target.value })} placeholder={org.legal_name ?? org.name} />
      </div>
      <div className="field">
        <label>Bank *</label>
        <select value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} required>
          <option value="">— pick a bank —</option>
          {['FNB', 'Standard Bank', 'ABSA', 'Nedbank', 'Capitec', 'Investec', 'TymeBank', 'Discovery Bank', 'Bank Zero', 'Other'].map((b) => <option key={b}>{b}</option>)}
        </select>
      </div>
      <div className="field">
        <label>Account type</label>
        <select value={form.account_type} onChange={(e) => setForm({ ...form, account_type: e.target.value })}>
          <option>Cheque</option><option>Savings</option><option>Business</option><option>Transmission</option>
        </select>
      </div>
      <div className="field">
        <label>Account number *</label>
        <input required value={form.account_number} onChange={(e) => setForm({ ...form, account_number: e.target.value })} placeholder="62123456789" />
      </div>
      <div className="field">
        <label>Branch / universal code</label>
        <input value={form.branch_code} onChange={(e) => setForm({ ...form, branch_code: e.target.value })} placeholder="250655" />
      </div>
      <div className="field col-span-2">
        <label>SWIFT (for international payments — optional)</label>
        <input value={form.swift} onChange={(e) => setForm({ ...form, swift: e.target.value })} placeholder="FIRNZAJJ" />
      </div>
      <div className="field col-span-2">
        <label>Reference text *</label>
        <input required value={form.reference_hint} onChange={(e) => setForm({ ...form, reference_hint: e.target.value })} placeholder="Your invoice number" />
        <p className="text-[11px] text-text-mute mt-1">What the customer should put in their EFT reference. We auto-fill the invoice number into this when displaying, so "Your invoice number" works well.</p>
      </div>

      {err && <div className="col-span-2 text-sm text-rust bg-rust/10 border border-rust/30 px-3 py-2 rounded-md">{err}</div>}
      {msg && <div className="col-span-2 text-sm text-emerald bg-emerald/10 border border-emerald/30 px-3 py-2 rounded-md">{msg}</div>}

      <div className="col-span-2 flex items-center justify-between pt-2">
        <div>
          {filled && (
            <button type="button" onClick={clearAll} disabled={busy} className="btn btn-ghost text-rust disabled:opacity-50">
              Clear all
            </button>
          )}
        </div>
        <button type="submit" disabled={busy || !loaded} className="btn btn-primary disabled:opacity-50">
          {busy ? 'Saving…' : 'Save'}
        </button>
      </div>

      {/* Live preview */}
      {filled && (
        <div className="col-span-2 mt-4 pt-4 border-t border-line">
          <div className="text-[10px] uppercase tracking-widest font-mono text-text-mute mb-2">Preview — what your customer sees</div>
          <div className="bg-paper text-text-paper rounded-lg p-5">
            <div className="font-serif text-base font-semibold mb-3" style={{ color: '#0c8f60' }}>How to pay</div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><div className="text-[10px] uppercase tracking-widest font-mono" style={{ color: 'rgba(15,21,33,0.55)' }}>Bank</div><div className="font-semibold">{form.bank_name}</div></div>
              <div><div className="text-[10px] uppercase tracking-widest font-mono" style={{ color: 'rgba(15,21,33,0.55)' }}>Account holder</div><div className="font-semibold">{form.account_holder}</div></div>
              <div><div className="text-[10px] uppercase tracking-widest font-mono" style={{ color: 'rgba(15,21,33,0.55)' }}>Account number</div><div className="font-mono font-semibold">{form.account_number}</div></div>
              <div><div className="text-[10px] uppercase tracking-widest font-mono" style={{ color: 'rgba(15,21,33,0.55)' }}>Branch code</div><div className="font-mono font-semibold">{form.branch_code || '—'}</div></div>
              <div><div className="text-[10px] uppercase tracking-widest font-mono" style={{ color: 'rgba(15,21,33,0.55)' }}>Account type</div><div className="font-semibold">{form.account_type}</div></div>
              <div className="col-span-2 pt-2 border-t border-emerald/25">
                <div className="text-[10px] uppercase tracking-widest font-mono" style={{ color: 'rgba(15,21,33,0.55)' }}>Use this reference</div>
                <div className="font-mono font-bold text-base mt-1">INV-2026-0001</div>
              </div>
            </div>
          </div>
        </div>
      )}
    </form>
  );
}

function BusinessTab({ org, onSaved }: { org: Organization; onSaved: () => void }) {
  const [form, setForm] = useState<Partial<Organization>>(org);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => { setForm(org); }, [org.id]); // eslint-disable-line react-hooks/exhaustive-deps

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true); setErr(null); setMsg(null);
    const { error } = await supabase.from('dodobooks_organizations').update({
      name: form.name,
      legal_name: form.legal_name || null,
      trading_as: form.trading_as || null,
      reg_number: form.reg_number || null,
      address: form.address || null,
      city: form.city || null,
      province: form.province || null,
      postal_code: form.postal_code || null,
      phone: form.phone || null,
      email: form.email || null,
      website: form.website || null,
      updated_at: new Date().toISOString(),
    }).eq('id', org.id);
    setBusy(false);
    if (error) setErr(error.message);
    else { setMsg('Saved.'); onSaved(); }
  }

  return (
    <form onSubmit={onSubmit} className="card grid grid-cols-2 gap-4">
      <div className="field col-span-2"><label>Trading name *</label><input required value={form.name ?? ''} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
      <div className="field col-span-2"><label>Legal name</label><input value={form.legal_name ?? ''} onChange={(e) => setForm({ ...form, legal_name: e.target.value })} /></div>
      <div className="field"><label>Reg. number</label><input value={form.reg_number ?? ''} onChange={(e) => setForm({ ...form, reg_number: e.target.value })} /></div>
      <div className="field"><label>Phone</label><input value={form.phone ?? ''} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
      <div className="field col-span-2"><label>Email</label><input type="email" value={form.email ?? ''} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
      <div className="field col-span-2"><label>Website</label><input value={form.website ?? ''} onChange={(e) => setForm({ ...form, website: e.target.value })} /></div>
      <div className="field col-span-2"><label>Street address</label><input value={form.address ?? ''} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
      <div className="field"><label>City</label><input value={form.city ?? ''} onChange={(e) => setForm({ ...form, city: e.target.value })} /></div>
      <div className="field"><label>Province</label><input value={form.province ?? ''} onChange={(e) => setForm({ ...form, province: e.target.value })} /></div>
      <div className="field"><label>Postal code</label><input value={form.postal_code ?? ''} onChange={(e) => setForm({ ...form, postal_code: e.target.value })} /></div>
      {err && <div className="col-span-2 text-sm text-rust bg-rust/10 border border-rust/30 px-3 py-2 rounded-md">{err}</div>}
      {msg && <div className="col-span-2 text-sm text-emerald bg-emerald/10 border border-emerald/30 px-3 py-2 rounded-md">{msg}</div>}
      <div className="col-span-2 flex justify-end pt-2">
        <button type="submit" disabled={busy} className="btn btn-primary disabled:opacity-50">{busy ? 'Saving…' : 'Save'}</button>
      </div>
    </form>
  );
}

function TaxTab({ org, onSaved }: { org: Organization; onSaved: () => void }) {
  const [isVat, setIsVat] = useState(org.is_vat_registered);
  const [vatNumber, setVatNumber] = useState(org.vat_number ?? '');
  const [vatPeriod, setVatPeriod] = useState<VatPeriod>(org.vat_period);
  const [vatBasis, setVatBasis] = useState(org.vat_basis);
  const [paye, setPaye] = useState(org.paye_number ?? '');
  const [uif, setUif] = useState(org.uif_number ?? '');
  const [fyStart, setFyStart] = useState(org.financial_year_start_month);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true); setErr(null); setMsg(null);
    const { error } = await supabase.from('dodobooks_organizations').update({
      is_vat_registered: isVat,
      vat_number: vatNumber || null,
      vat_period: vatPeriod,
      vat_basis: vatBasis,
      paye_number: paye || null,
      uif_number: uif || null,
      financial_year_start_month: fyStart,
      updated_at: new Date().toISOString(),
    }).eq('id', org.id);
    setBusy(false);
    if (error) setErr(error.message);
    else { setMsg('Saved.'); onSaved(); }
  }

  return (
    <form onSubmit={onSubmit} className="card grid grid-cols-2 gap-4">
      <div className="col-span-2 flex items-center gap-3 bg-ink-2 border border-line rounded-md px-3 py-2">
        <input id="vat-reg" type="checkbox" className="accent-emerald w-4 h-4" checked={isVat} onChange={(e) => setIsVat(e.target.checked)} />
        <label htmlFor="vat-reg" className="text-sm font-sans normal-case tracking-normal text-text">VAT registered with SARS</label>
      </div>
      <div className="field"><label>VAT number</label><input disabled={!isVat} value={vatNumber} onChange={(e) => setVatNumber(e.target.value)} /></div>
      <div className="field"><label>VAT period</label>
        <select disabled={!isVat} value={vatPeriod} onChange={(e) => setVatPeriod(e.target.value as VatPeriod)}>
          <option value="monthly">Monthly</option>
          <option value="two_monthly">Two-monthly</option>
          <option value="six_monthly">Six-monthly</option>
          <option value="annual">Annual</option>
        </select>
      </div>
      <div className="field"><label>VAT basis</label>
        <select disabled={!isVat} value={vatBasis} onChange={(e) => setVatBasis(e.target.value as any)}>
          <option value="invoice">Invoice basis (default)</option>
          <option value="payment">Payment basis</option>
        </select>
      </div>
      <div className="field"><label>Financial year start month</label>
        <select value={fyStart} onChange={(e) => setFyStart(Number(e.target.value))}>
          {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
            <option key={m} value={m}>{new Date(2000, m - 1).toLocaleString('en-ZA', { month: 'long' })}</option>
          ))}
        </select>
        <p className="text-[11px] text-text-mute mt-1">SA tax year is March → February. Most businesses use March.</p>
      </div>
      <div className="field"><label>PAYE number</label><input value={paye} onChange={(e) => setPaye(e.target.value)} /></div>
      <div className="field"><label>UIF number</label><input value={uif} onChange={(e) => setUif(e.target.value)} /></div>
      {err && <div className="col-span-2 text-sm text-rust bg-rust/10 border border-rust/30 px-3 py-2 rounded-md">{err}</div>}
      {msg && <div className="col-span-2 text-sm text-emerald bg-emerald/10 border border-emerald/30 px-3 py-2 rounded-md">{msg}</div>}
      <div className="col-span-2 flex justify-end pt-2">
        <button type="submit" disabled={busy} className="btn btn-primary disabled:opacity-50">{busy ? 'Saving…' : 'Save'}</button>
      </div>
    </form>
  );
}

function NewOrgTab({ createOrg }: { createOrg: ReturnType<typeof useOrg>['createOrg'] }) {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true); setErr(null);
    const { error } = await createOrg({ name: name.trim() });
    setBusy(false);
    if (error) setErr(error);
    else setName('');
  }
  return (
    <form onSubmit={onSubmit} className="card">
      <p className="text-sm text-text-dim mb-4">Add another business to manage. It'll be fully isolated — its own chart of accounts, contacts, ledger, and VAT.</p>
      <div className="field max-w-sm">
        <label>Business name</label>
        <input required value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Side Hustle (Pty) Ltd" />
      </div>
      {err && <div className="text-sm text-rust bg-rust/10 border border-rust/30 px-3 py-2 rounded-md mt-3">{err}</div>}
      <button type="submit" disabled={busy || !name.trim()} className="btn btn-primary mt-4 disabled:opacity-50"><Plus size={14} /> Create organization</button>
    </form>
  );
}
